import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { toastSuccess, toastWarning } from "@/utils/toast";
import { Popup, Checkbox, Toast, Picker, DatePicker } from "antd-mobile";
import InputAudio from "@/components/InputAudio";
import { uploadImage } from "@/api/upload";
import {
  getStaffDetail,
  getStaffConfig,
  getCityList,
  saveStaffData,
} from "@/api/staff";

const BRAND = "#FF6B35";
const BRAND_GRADIENT = "linear-gradient(135deg, #FF6B35, #FF9A5C)";

// ═══ 选项数据═══
const SEX_OPTIONS = [
  { label: "未知", value: 0 },
  { label: "男", value: 1 },
  { label: "女", value: 2 },
];

const MARITAL_OPTIONS = ["单身", "非单身", "已婚"];

const EDU_OPTIONS = ["高中及以下", "大专", "本科", "硕士", "博士", "其他"];

const CONSTELLATION_OPTIONS = [
  "白羊座","金牛座","双子座","巨蟹座","狮子座","处女座",
  "天秤座","天蝎座","射手座","摩羯座","水瓶座","双鱼座",
];

// 标签（从配置接口获取）
interface LabelOption {
  id?: number;
  name: string;
}

// 城市列表项
interface CityItem {
  city_id: number;
  city_name: string;
}

export default function ProfileEditPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // ── 表单状态 ──
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [thumb, setThumb] = useState("");
  const [idCardNo, setIdCardNo] = useState("");
  const [sex, setSex] = useState(0);
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [cityId, setCityId] = useState(0);
  const [relationship, setRelationship] = useState("");
  const [education, setEducation] = useState("");
  const [starSigns, setStarSigns] = useState("");
  const [birthday, setBirthday] = useState("");
  const [serviceLabel, setServiceLabel] = useState<string[]>([]);
  const [resume, setResume] = useState("");

  // ── 下拉选项数据 ──
  const [labelOptions, setLabelOptions] = useState<LabelOption[]>([]);
  const [cityList, setCityList] = useState<CityItem[]>([]);

  // ── 录音相关（InputAudio 公共组件） ──
  const [audioUrl, setAudioUrl] = useState("");
  const [showInputAudio, setShowInputAudio] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // ── 头像上传 ref ──
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // ── InputAudio 完成回调 ──
  const handleAudioConfirm = async (data: { blob: Blob; url: string; duration: number }) => {
    setAudioUrl(data.url);
    setShowInputAudio(false);
    if (audioRef.current) audioRef.current.src = data.url;
    // TODO: 上传音频到服务端
  };

  // ── 弹窗状态 ──
  const [showLabelPopup, setShowLabelPopup] = useState(false);

  // Picker 弹出状态
  const [showSexPicker, setShowSexPicker] = useState(false);
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [showMaritalPicker, setShowMaritalPicker] = useState(false);
  const [showEduPicker, setShowEduPicker] = useState(false);
  const [showConstellationPicker, setShowConstellationPicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // 当前选中值（用于 Picker 显示）
  const sexIndex = SEX_OPTIONS.findIndex((o) => o.value === sex);
  const cityIndex = Math.max(0, cityList.findIndex((c) => c.city_id === cityId));
  const maritalIndex = relationship ? MARITAL_OPTIONS.indexOf(relationship) : -1;
  const eduIndex = education ? EDU_OPTIONS.indexOf(education) : -1;
  const constellationIndex = starSigns ? CONSTELLATION_OPTIONS.indexOf(starSigns) : -1;

  // ── 加载达人详情 + 配置 ──
  useEffect(() => {
    const loadAll = async () => {
      try {
        const [detail, config, cityResp] = await Promise.all([
          getStaffDetail(),
          getStaffConfig(),
          getCityList(),
        ]);

        const d = detail;
        setName(d.name || "");
        setPhone(d.phone || "");
        setThumb(d.thumb || "");
        setIdCardNo((d as any).id_card_no || "");
        setSex(d.sex ?? 0);
        setHeight(String((d as any).staff_height || ""));
        setWeight(String((d as any).staff_weight || ""));
        setCityId((d as any).city_id ?? 0);
        setRelationship((d as any).relationship || "");
        setEducation((d as any).education || "");
        setStarSigns((d as any).star_signs || "");
        setBirthday((d as any).birthday || "");
        setServiceLabel(((d as any).service_label && Array.isArray((d as any).service_label)
          ? (d as any).service_label : []));
        setResume(d.resume || "");
        setAudioUrl((d as any).content_audio || "");

        // 城市列表（/city/list 返回结构：{ stats, msg, data: [{city_id,parent_id,city_name}], status }）
        const cityBody = (cityResp as any)?.data;
        let rawCities: any[] | null = null;

        if (Array.isArray(cityBody)) {
          rawCities = cityBody;
        } else if (Array.isArray(cityBody?.data)) {
          rawCities = cityBody.data;
        } else if (Array.isArray(cityBody?.list)) {
          rawCities = cityBody.list;
        }

        if (rawCities?.length) {
          setCityList(rawCities.map((c: any) => ({
            city_id: (Number(c.city_id) || c.id || 0),
            city_name: c.city_name || c.name || "",
          })));
        }

        // 服务标签
        if ((config as any)?.massage_service_label) {
          setLabelOptions((config as any).massage_service_label || []);
        }
      } catch (e) {
        console.error("[ProfileEdit] 加载失败:", e);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  // ── 提交 ──
  const handleSubmit = async () => {
    if (!phone.trim()) return toastWarning("请输入手机号");
    if (!/^1[3-9]\d{9}$/.test(phone)) return toastWarning("手机号格式不正确");

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("phone", phone);
      fd.append("thumb", thumb);
      fd.append("idcard_no", idCardNo);
      fd.append("sex", String(sex));
      fd.append("staff_height", height);
      fd.append("staff_weight", weight);
      fd.append("city_id", String(cityId));
      fd.append("relationship", relationship);
      fd.append("education", education);
      fd.append("star_signs", starSigns);
      fd.append("birthday", birthday);
      fd.append("service_label", JSON.stringify(serviceLabel));
      fd.append("resume", resume);
      fd.append("content_audio", audioUrl);

      await saveStaffData(fd);
      toastSuccess("保存成功");
      setTimeout(() => navigate(-1), 800);
    } catch {/* 错误已在 request 层统一 toast */} finally {
      setSubmitting(false);
    }
  };

  // ── 头像上传 ──
  const handleAvatarUpload = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = avatarInputRef.current;
    if (!inputEl?.files?.length) return;

    try {
      Toast.show({ icon: "loading", content: "上传中..." });
      const url = await uploadImage(inputEl.files[0]);
      setThumb(url);
      Toast.show({ icon: "success", content: "头像更新成功", duration: 1500 });
    } catch {/* uploadImage 内已 toast */} finally {
      if (inputEl) inputEl.value = "";
    }
  };

  // ── 播放控制 ──
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  // Loading 状态
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F7F7F7" }}>
        <i className="ri-loader-4-line text-2xl animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+112px)]" style={{ background: "#F7F7F7", fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}>
      {/* 隐藏文件 input */}
      <input ref={avatarInputRef} type="file" accept="image/*"
        className="sr-only" aria-hidden tabIndex={-1}
        onChange={handleAvatarChange} />

      {/* Header */}
      <div className="bg-white px-5 flex items-center gap-3" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)", paddingBottom: 13, borderBottom: "1px solid #F5F5F5" }}>
        <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] shrink-0" style={{ background: "#F3F4F6" }}>
          <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#374151" }} />
        </button>
        <span className="text-lg font-bold text-gray-800">个人资料</span>
      </div>

      <div className="px-4 pt-4 space-y-3 pb-24">
        {/* 头像区域 */}
        <div className="flex flex-col items-center gap-3 pt-2">
          <div className="relative w-[88px] h-[88px]" onClick={handleAvatarUpload}>
            <img src={thumb || ""} alt="" className="w-full h-full rounded-full object-cover bg-gray-100" />
            <div className="absolute right-0 bottom-0 w-[36px] h-[36px] rounded-full flex items-center justify-center cursor-pointer active:scale-[0.95]"
              style={{ background: BRAND }}>
              <i className="ri-camera-line text-white" style={{ fontSize: 16 }} />
            </div>
          </div>
          <span className="text-base font-bold">{name || "未设置"}</span>
        </div>

        {/* 表单卡片 */}
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="bg-white rounded-2xl overflow-hidden">
          <div className="divide-y divide-gray-100">
            <FormRow label="姓名" required>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="请输入姓名"
                className="w-full py-2.5 px-3 bg-transparent outline-none text-sm font-medium text-right text-gray-900" />
            </FormRow>

            <FormRow label="手机号" required>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="11位手机号"
                className={`w-full py-2.5 px-3 bg-transparent outline-none text-sm font-medium text-right ${
                  /^\d+$/.test(phone) && phone.length >= 11 && !/^1[3-9]\d{9}$/.test(phone) ? "border border-red-300 bg-red-50" : ""
                } ${!phone ? "text-gray-400" : "text-gray-900"}`} />
            </FormRow>

            <FormRow label="身份证号">
              <input type="text" value={idCardNo} onChange={(e) => setIdCardNo(e.target.value)} placeholder="选填"
                className="w-full py-2.5 px-3 bg-transparent outline-none text-sm font-medium text-right text-gray-900" />
            </FormRow>

            <FormRow label="身高(cm)">
              <input type="number" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="cm"
                className="w-full py-2.5 px-3 bg-transparent outline-none text-sm font-medium text-right text-gray-900" />
            </FormRow>

            <FormRow label="体重(kg)">
              <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="kg"
                className="w-full py-2.5 px-3 bg-transparent outline-none text-sm font-medium text-right text-gray-900" />
            </FormRow>

            <FormRow label="性别">
              <button type="button" onClick={() => setShowSexPicker(true)}
                className="w-full py-2.5 px-3 rounded-xl bg-gray-50 outline-none text-sm font-medium text-right active:bg-gray-100 transition-colors"
              >
                {SEX_OPTIONS.find((o) => o.value === sex)?.label || "请选择"}
                <i className="ri-arrow-down-s-line ml-1 text-xs text-gray-400" />
              </button>
            </FormRow>

            <FormRow label="城市">
              <button type="button" onClick={() => cityList.length > 0 && setShowCityPicker(true)}
                disabled={cityList.length === 0}
                className={`w-full py-2.5 px-3 rounded-xl outline-none text-sm font-medium text-right active:bg-gray-100 transition-colors ${cityList.length === 0 ? "bg-gray-100 text-gray-400" : "bg-gray-50 text-gray-900"}`}>
                {cityList.find((c) => c.city_id === cityId)?.city_name || (cityList.length === 0 ? "加载中..." : "请选择")}
                {cityList.length > 0 && <i className="ri-arrow-down-s-line ml-1 text-xs text-gray-400" />}
              </button>
            </FormRow>

            <FormRow label="情感状态">
              <button type="button" onClick={() => setShowMaritalPicker(true)}
                className="w-full py-2.5 px-3 rounded-xl bg-gray-50 outline-none text-sm font-medium text-right active:bg-gray-100 transition-colors"
              >
                {relationship || "请选择"}
                <i className="ri-arrow-down-s-line ml-1 text-xs text-gray-400" />
              </button>
            </FormRow>

            <FormRow label="学历">
              <button type="button" onClick={() => setShowEduPicker(true)}
                className="w-full py-2.5 px-3 rounded-xl bg-gray-50 outline-none text-sm font-medium text-right active:bg-gray-100 transition-colors"
              >
                {education || "请选择"}
                <i className="ri-arrow-down-s-line ml-1 text-xs text-gray-400" />
              </button>
            </FormRow>

            <FormRow label="星座">
              <button type="button" onClick={() => setShowConstellationPicker(true)}
                className="w-full py-2.5 px-3 rounded-xl bg-gray-50 outline-none text-sm font-medium text-right active:bg-gray-100 transition-colors"
              >
                {starSigns || "请选择"}
                <i className="ri-arrow-down-s-line ml-1 text-xs text-gray-400" />
              </button>
            </FormRow>

            <FormRow label="出生日期">
              <button type="button" onClick={() => setShowDatePicker(true)}
                className="w-full py-2.5 px-3 rounded-xl bg-gray-50 outline-none text-sm font-medium text-right active:bg-gray-100 transition-colors"
              >
                {birthday || "请选择"}
                <i className="ri-arrow-down-s-line ml-1 text-xs text-gray-400" />
              </button>
            </FormRow>

            {/* 服务标签 — 多选（最多3个） */}
            <div className="bg-white rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-gray-900">服务标签</span>
                <button type="button" onClick={() => setShowLabelPopup(true)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold active:scale-[0.98] transition-all"
                  style={{ color: BRAND, background: "#FFF3EE" }}>
                  {serviceLabel.length > 0 ? `已选(${serviceLabel.length}/3)` : "选择标签"}
                </button>
              </div>
              {serviceLabel.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {serviceLabel.map((label, i) => (
                    <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{ color: BRAND, background: `${BRAND}15` }}>
                      {label}
                      <button type="button" onClick={() => setServiceLabel(serviceLabel.filter((l) => l !== label))} className="ml-1 w-4 h-4 inline-flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40">
                        <i className="ri-close-line text-xs" style={{ color: BRAND }} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* 自我介绍 */}
            <div className="px-4 py-3 border-b border-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-500">自我介绍</span>
                <span className="text-xs text-gray-400 tabular-nums">{resume.length}/200</span>
              </div>
              <textarea value={resume} onChange={(e) => setResume(e.target.value)} rows={5} maxLength={200}
                placeholder="介绍一下自己吧..."
                className="w-full py-3 px-3 rounded-xl bg-gray-50 outline-none text-sm resize-none leading-relaxed" />
            </div>

            {/* 语音介绍 */}
            <FormRow label="语音介绍">
              {!audioUrl ? (
                <button type="button" onClick={() => setShowInputAudio(true)}
                  className="w-full flex items-center gap-2.5 py-2.5 px-4 rounded-xl border border-dashed border-gray-200 active:bg-gray-50 transition-colors"
                  style={{ color: BRAND }}>
                  <i className="ri-mic-line text-base" />
                  <span className="text-sm font-medium">点击录制语音介绍</span>
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2.5 py-2 px-4 rounded-xl" style={{ border: `1px solid ${BRAND}30`, background: `${BRAND}08` }}>
                    <div className="w-[44px] h-[44px] rounded-full bg-white/90 flex items-center justify-center shrink-0">
                      <i className="ri-mic-fill" style={{ color: BRAND, fontSize: 18 }} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: BRAND }}>语音介绍</span>
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    <button type="button" onClick={togglePlay}
                      className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center shrink-0 hover:bg-white cursor-pointer">
                      <i className={`${playing ? "ri-pause-circle-fill" : "ri-play-fill-line"} text-base`} style={{ color: BRAND }} />
                    </button>
                    <button type="button" onClick={() => setShowInputAudio(true)}
                      className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center shrink-0 hover:bg-white cursor-pointer">
                      <i className="ri-replay-line text-base" style={{ color: BRAND }} />
                    </button>
                  </div>
                  {audioUrl && (
                    <audio ref={audioRef} src={audioUrl} onEnded={() => setPlaying(false)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}>
                      您的浏览器不支持音频播放。
                    </audio>
                  )}
                </div>
              )}
            </FormRow>
          </div>

          {/* 底部提交按钮 */}
          <div className="fixed bottom-0 left-0 right-0 pr-4 pl-4 bg-white shadow-upward z-40"
            style={{ paddingTop: "8px", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))" }}>
            <button type="submit" disabled={submitting}
              className={`w-full py-3.5 rounded-2xl text-sm font-semibold cursor-pointer active:scale-[0.98] transition-all ${submitting ? "opacity-50" : ""}`}
              style={{ background: BRAND_GRADIENT, color: "#fff" }}>
              {submitting ? "保存中..." : "确认修改"}
            </button>
          </div>
        </form>
      </div>

      {/* 标签选择弹窗（antd-mobile Popup） */}
      <Popup visible={showLabelPopup} onMaskClick={() => setShowLabelPopup(false)} position="bottom"
        bodyStyle={{ borderRadius: "16px 16px 0 0", maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-base font-bold text-gray-900">选择标签</h3>
            <span className="text-xs text-gray-400">最多可选 3 个</span>
          </div>

          <div className="overflow-y-auto flex-1 space-y-1 pb-2" style={{ minHeight: 120 }}>
            {labelOptions.map((item) => {
              const checked = serviceLabel.includes(item.name);
              return (
                <label key={item.name} onClick={() => {
                  if (checked) { setServiceLabel(serviceLabel.filter((l) => l !== item.name)); }
                  else {
                    if (serviceLabel.length >= 3) { Toast.show({ content: "最多选 3 个标签" }); return; }
                    setServiceLabel([...serviceLabel, item.name]);
                  }
                }}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl active:bg-orange-50 cursor-pointer">
                  <Checkbox checked={checked}
                    style={{ "--icon-size": "18px", "--color": BRAND, "--checked-icon-color": "#fff" } as React.CSSProperties} />
                  <span className="text-sm text-gray-800">{item.name}</span>
                </label>
              );
            })}
          </div>

          <button onClick={() => setShowLabelPopup(false)}
            className="w-full py-2.5 rounded-2xl text-sm font-semibold active:scale-[0.98] cursor-pointer"
            style={{ background: BRAND, color: "#fff" }}>
            确认 ({serviceLabel.length})
          </button>
        </div>
      </Popup>

      {/* ═══ Picker 弹窗（底部弹出选择） ═══ */}
      {/* 性别 Picker */}
      <Picker
        columns={[SEX_OPTIONS.map((o) => ({ label: o.label, value: o.value }))]}
        visible={showSexPicker}
        value={[sex]}
        onClose={() => setShowSexPicker(false)}
        onConfirm={(val) => {
          const v = val?.[0];
          if (typeof v === "number") setSex(v);
          setShowSexPicker(false);
        }}
      />

      {/* 城市 Picker */}
      <Picker
        columns={[cityList.map((c) => ({ label: c.city_name, value: c.city_id }))]}
        visible={showCityPicker}
        value={[cityId]}
        onClose={() => setShowCityPicker(false)}
        onConfirm={(val) => {
          const v = val?.[0];
          if (typeof v === "number") setCityId(v);
          setShowCityPicker(false);
        }}
      />

      {/* 情感状态 Picker */}
      <Picker
        columns={[MARITAL_OPTIONS.map((m) => ({ label: m, value: m }))]}
        visible={showMaritalPicker}
        value={maritalIndex >= 0 ? [MARITAL_OPTIONS[maritalIndex]] : [""]}
        onClose={() => setShowMaritalPicker(false)}
        onConfirm={(val) => {
          const v = val?.[0];
          if (typeof v === "string") setRelationship(v);
          setShowMaritalPicker(false);
        }}
      />

      {/* 学历 Picker */}
      <Picker
        columns={[EDU_OPTIONS.map((e) => ({ label: e, value: e }))]}
        visible={showEduPicker}
        value={eduIndex >= 0 ? [EDU_OPTIONS[eduIndex]] : [""]}
        onClose={() => setShowEduPicker(false)}
        onConfirm={(val) => {
          const v = val?.[0];
          if (typeof v === "string") setEducation(v);
          setShowEduPicker(false);
        }}
      />

      {/* 星座 Picker */}
      <Picker
        columns={[CONSTELLATION_OPTIONS.map((c) => ({ label: c, value: c }))]}
        visible={showConstellationPicker}
        value={constellationIndex >= 0 ? [CONSTELLATION_OPTIONS[constellationIndex]] : [""]}
        onClose={() => setShowConstellationPicker(false)}
        onConfirm={(val) => {
          const v = val?.[0];
          if (typeof v === "string") setStarSigns(v);
          setShowConstellationPicker(false);
        }}
      />

      {/* 日期选择器 */}
      <DatePicker
        visible={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onConfirm={(date) => { setBirthday(date.toISOString().split("T")[0]); setShowDatePicker(false); }}
        max={new Date()}
        defaultValue={birthday ? new Date(birthday) : undefined}
      />

      {/* InputAudio 公共录音组件 */}
      <InputAudio open={showInputAudio} onClose={() => setShowInputAudio(false)} onConfirm={handleAudioConfirm} value={audioUrl || undefined} />
    </div>
  );
}

/** 表单行组件 */
function FormRow({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div className="flex items-center gap-3 py-3 px-4 border-b border-gray-50 last:border-b-0">
      <span className="text-sm font-medium text-gray-500 w-[72px] shrink-0">{label}</span>
      {children}
    </div>
  );
}
